// The `heraldic` structure symbol set: card 03 of mockup round 054,
// "HERALDIC SHIELD" (R1103, R1104, `ui-map-interface.md` §6.2).
//
// One shield outline carries all nine kinds. The field takes the holder's
// ink and the charge takes the argent of the palette: mast, chimney, fronds,
// tube, crown, cradle, keyhole, columns and coral. The Trench Throne carries
// the larger shield, because it is the capital.
//
// The contested tell is the field parted per pale: the dexter half keeps the
// holder's ink and the sinister half takes the attacker's ink with the
// hatching of gules, so the tincture reads in greyscale. Card 03 creeps the
// split line with the clock; the atlas bakes one still cell, so the line
// stands at the middle phase of that creep (R1104).
//
// **The shield is the clip, and there is no clip.** `MarkGraphics` carries
// no clip path, so the sinister half states its own geometry: `rightAt()`
// gives the right edge of the shield at one depth and `bottomAt()` gives the
// depth the edge reaches at one abscissa, both solved from the quadratic the
// card draws the shield with. The half is then one polygon and every hatch
// line is one segment, each already inside the field.
//
// Every offset, weight, radius and alpha below is the figure card 03 draws.

import type { MarkGraphics } from '../marks';
import {
  arcPath, cubic, disc, line, PALE as ARGENT, quadratic, ring, rot, stroke, TILT,
  setOf, type StructureDraw, type StructureInk, type StructureKind, type StructureSet,
} from './shared';

/** The scale of the Throne's shield. Every other kind draws at 1. */
const THRONE_SCALE = 1.2;

/** The jagged pale: the two abscissas it steps between, and the step. */
const JAG_LEFT = -1.8;
const JAG_RIGHT = 2.2;
const JAG_STEP = 4;
/** The middle phase of the eight-unit creep card 03 runs on the clock. */
const JAG_PHASE = 4;

/** The gap between two hatch lines of gules. */
const HATCH_GAP = 3;

/**
 * The shield outline at one scale, as the point list of one polygon.
 *
 * It is `shield()` of card 03: a flat chief, two straight flanks, and the
 * base closed by two quadratic curves that meet at the point.
 */
function shieldPoints(k: number): number[] {
  const pts = [-13 * k, -14 * k, 13 * k, -14 * k, 13 * k, -2 * k];
  quadratic(pts, 13 * k, -2 * k, 13 * k, 11 * k, 0, 17 * k, 14);
  quadratic(pts, 0, 17 * k, -13 * k, 11 * k, -13 * k, -2 * k, 14);
  return pts;
}

/**
 * The right edge of the shield at one depth.
 *
 * Above the base the edge is the straight flank at 13k. Below it the edge is
 * the curve `x(t) = 13k(1 - t²)`, `y(t) = k(-7t² + 26t - 2)`. The depth
 * gives `t` through the second equation, and `t` gives the abscissa.
 */
function rightAt(y: number, k: number): number {
  if (y <= -2 * k) return 13 * k;
  const d = 676 - 28 * (2 + y / k);
  if (d <= 0) return 0;
  const t = (26 - Math.sqrt(d)) / 14;
  return 13 * k * (1 - t * t);
}

/** The depth the right edge of the shield reaches at one abscissa. */
function bottomAt(x: number, k: number): number {
  const t = Math.sqrt(Math.max(0, 1 - x / (13 * k)));
  return k * (-7 * t * t + 26 * t - 2);
}

/** The jagged pale, from above the chief to below the point. */
function jagPoints(k: number): number[] {
  const out: number[] = [];
  for (let y = -16 * k + JAG_PHASE - 8; y <= 19 * k; y += JAG_STEP) {
    out.push(Math.round((y + 8) / JAG_STEP) & 1 ? JAG_RIGHT : JAG_LEFT, y);
  }
  return out;
}

/** Where the jagged pale stands at one depth. */
function jagAt(jag: number[], y: number): number {
  const n = jag.length / 2;
  if (y <= jag[1]) return jag[0];
  for (let m = 1; m < n; m++) {
    const y1 = jag[2 * m + 1];
    if (y <= y1) {
      const y0 = jag[2 * m - 1], x0 = jag[2 * m - 2], x1 = jag[2 * m];
      return x0 + ((x1 - x0) * (y - y0)) / (y1 - y0);
    }
  }
  return jag[2 * n - 2];
}

/**
 * The sinister half of the field, as one polygon.
 *
 * The walk goes down the jagged pale to the depth where the pale meets the
 * right edge, then back up that edge to the top corner. `jag` is how many
 * numbers of `pts` belong to the pale, so the caller strokes the split line
 * from the same list.
 */
function sinisterHalf(k: number): { pts: number[]; jag: number } {
  const jag = jagPoints(k);
  const top = -14 * k, tip = 17 * k;
  const pts: number[] = [jagAt(jag, top), top];
  let last = top;
  for (let m = 0; m < jag.length / 2; m++) {
    const x = jag[2 * m], y = jag[2 * m + 1];
    if (y <= top) continue;
    if (y >= tip || x >= rightAt(y, k)) break;
    pts.push(x, y);
    last = y;
  }
  // Where the pale meets the right edge, by bisection between the last point
  // inside the field and the point of the shield.
  let lo = last, hi = tip;
  for (let i = 0; i < 24; i++) {
    const m = (lo + hi) / 2;
    if (jagAt(jag, m) < rightAt(m, k)) lo = m; else hi = m;
  }
  const edge = rightAt(lo, k);
  pts.push(Math.max(-edge, Math.min(jagAt(jag, lo), edge)), lo);
  const jagLen = pts.length;
  // Back up the right edge of the shield, then to the top corner.
  const steps = 14;
  for (let i = steps; i >= 0; i--) {
    const y = -2 * k + (lo + 2 * k) * (i / steps);
    pts.push(rightAt(y, k), y);
  }
  pts.push(13 * k, top);
  return { pts, jag: jagLen };
}

/**
 * The charge of one kind, in the argent, at the scale of the shield.
 *
 * It is `charge()` of card 03. The card scales the context by the shield's
 * scale, so every offset AND every line width takes the scale here.
 */
function charge(
  g: MarkGraphics, kind: StructureKind, a: number, k: number,
): void {
  const color = ARGENT;
  const L = (x0: number, y0: number, x1: number, y1: number, w: number):
  void => { line(g, x0 * k, y0 * k, x1 * k, y1 * k, color, a, w * k); };
  const D = (x: number, y: number, r: number): void => {
    disc(g, x * k, y * k, r * k, color, a);
  };
  const PL = (pts: number[], close?: boolean): void => {
    g.poly(pts.map((v) => v * k), close);
  };
  const S = (w = 1.3): void => { stroke(g, color, a, w * k); };
  switch (kind) {
    case 'spire': {
      // The mast, with two radio arcs over its head.
      L(0, 9, 0, -3, 1.4); L(-4, 9, 4, 9, 1.3); D(0, -4, 1.3);
      for (const r of [4, 7]) {
        arcPath(g, 0, -4 * k, r * k, -2.5, -0.65);
        S(1.1);
      }
      break;
    }
    case 'forge': {
      // The chimney on its hearth, with one smoke curl.
      PL([-6, 9, -3, -2, 3, -2, 6, 9], false); S();
      L(-8, 9, 8, 9, 1.3);
      const curl = [0, -4];
      cubic(curl, 0, -4, 4, -6, -4, -8, 0, -10);
      PL(curl, false); S(1.1);
      break;
    }
    case 'kelp': {
      // Three fronds.
      const fronds = [
        [-5, 10, -10, 1, -6, -7],
        [0, 10, 4, 0, 0, -10],
        [5, 10, 10, 2, 6, -6],
      ];
      for (const [x0, y0, cx, cy, x1, y1] of fronds) {
        const pts = [x0, y0];
        quadratic(pts, x0, y0, cx, cy, x1, y1);
        PL(pts, false); S(1.2);
      }
      break;
    }
    case 'battery': {
      // The heavy tube on its mount. The card tilts the context, so the two
      // ends of the tube carry the tilt.
      const [x0, y0] = rot(-8, -1, TILT), [x1, y1] = rot(7, -1, TILT);
      L(x0, y0, x1, y1, 3.4);
      PL([-6, 9, -2, 3, 2, 3, 6, 9], false); S();
      break;
    }
    case 'throne': {
      // The crown over the dais, with the jewel above it.
      PL([-8, 4, -8, -7, -4, -2, 0, -9, 4, -2, 8, -7, 8, 4]); S(1.4);
      L(-8, 8, 8, 8, 1.4); D(0, -9, 1.2);
      break;
    }
    case 'drydock': {
      // The cradle with a hull in it.
      const cradle = [-10, -3, -8, 6];
      quadratic(cradle, -8, 6, 0, 9, 8, 6);
      cradle.push(10, -3);
      PL(cradle, false); S();
      const hull = [-6, -2];
      quadratic(hull, -6, -2, 0, 6, 6, -2);
      PL(hull); S();
      break;
    }
    case 'archive': {
      // The keyhole.
      ring(g, 0, -3 * k, 3.2 * k, color, a, 1.4 * k);
      PL([-1.6, -1, -3.2, 8, 3.2, 8, 1.6, -1]);
      g.fill({ color, alpha: a });
      break;
    }
    case 'marshal': {
      // The pediment over three columns.
      PL([-9, -3, 0, -9, 9, -3]); S(1.2);
      for (const x of [-6, 0, 6]) L(x, -3, x, 7, 1.3);
      L(-9, 7, 9, 7, 1.3);
      break;
    }
    case 'ward': {
      // The coral fan, with a bud on every tip.
      L(0, 9, 0, 2, 1.3);
      for (const [x, y] of [[-7, -4], [-2.5, -8], [2.5, -8], [7, -4]]) {
        L(0, 2, x, y, 1.2); D(x, y, 1.2);
      }
      break;
    }
  }
}

/** One kind of the `heraldic` set, still. */
function markOf(kind: StructureKind): StructureDraw {
  const k = kind === 'throne' ? THRONE_SCALE : 1;
  const outline = shieldPoints(k);
  const half = sinisterHalf(k);
  return (g: MarkGraphics, ink: StructureInk): void => {
    g.poly(outline);
    g.fill({ color: ink.own, alpha: ink.held ? 0.3 : 0.12 });
    if (ink.foe !== 0) {
      g.poly(half.pts);
      g.fill({ color: ink.foe, alpha: 0.5 });
      // The hatching of gules. Every line shares its style, so the half
      // takes one batch and one stroke (`ui-map-interface.md` §10).
      for (let x = HATCH_GAP; x < 13 * k; x += HATCH_GAP) {
        g.moveTo(x, -14 * k).lineTo(x, bottomAt(x, k));
      }
      g.stroke({ width: 0.8, color: ink.foe, alpha: 0.7 });
      g.poly(half.pts.slice(0, half.jag), false);
      stroke(g, ink.foe, 1, 1.3);
    }
    g.poly(outline);
    g.stroke({ width: 1.4, color: ink.own, alpha: ink.held ? 0.95 : 0.6 });
    charge(g, kind, ink.held ? 0.95 : 0.55, k);
  };
}

/** Card 03, all nine kinds. */
export const HERALDIC_SET: StructureSet = setOf(markOf);
